import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronRight, Sprout, Factory, ShieldCheck, Leaf, Users, Truck, MapPin } from "lucide-react";
import { HERO_SLIDES, VERTICALS, IMPACT_STATS, WHY_CHOOSE, LEADERSHIP, BRANCHES } from "@/lib/data";
import StatsCounter from "@/components/StatsCounter";

const ICONS = { Sprout, Factory, ShieldCheck, Leaf, Users, Truck };

export default function Home() {
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(id);
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100svh] w-full overflow-hidden bg-shramik-ink" data-testid="hero-section">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide}
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="absolute inset-0"
          >
            <img src={HERO_SLIDES[slide].image} alt={HERO_SLIDES[slide].label} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-shramik-ink/85 via-shramik-ink/55 to-shramik-ink/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-shramik-ink/70 to-transparent" />
          </motion.div>
        </AnimatePresence>

        <div className="relative container-page pt-[140px] md:pt-[180px] pb-24 min-h-[100svh] flex flex-col justify-between">
          <div className="max-w-4xl">
            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6, delay:0.2}} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-white/90 text-xs uppercase tracking-[0.22em] mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-shramik-gold animate-pulse" />
              {HERO_SLIDES[slide].label} — Since 2004
            </motion.div>
            <motion.h1
              key={`t-${slide}`}
              initial={{opacity:0,y:30}} animate={{opacity:1,y:0}} transition={{duration:0.7}}
              className="font-display font-light text-white text-5xl md:text-7xl lg:text-[92px] leading-[0.98] tracking-tighter text-balance"
            >
              Building rural prosperity through <em className="not-italic font-serif italic text-shramik-gold">innovation</em>, agriculture, industry & infrastructure.
            </motion.h1>
            <motion.p initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.7, delay:0.15}}
              className="mt-8 text-white/85 text-lg md:text-xl max-w-2xl leading-relaxed">
              Empowering farmers. Strengthening communities. Delivering excellence across five business verticals.
            </motion.p>
            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.7, delay:0.3}} className="mt-10 flex flex-wrap gap-4">
              <Link to="/businesses" data-testid="hero-cta-businesses" className="btn-primary bg-white text-shramik-ink hover:bg-shramik-gold hover:text-shramik-ink">
                Explore Our Businesses <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" data-testid="hero-cta-contact" className="btn-secondary border-white text-white hover:bg-white/10">
                Contact Us <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>

          {/* Slide indicators */}
          <div className="flex items-center gap-8 mt-16">
            <div className="flex gap-2">
              {HERO_SLIDES.map((_, i) => (
                <button key={i} onClick={() => setSlide(i)} data-testid={`hero-dot-${i}`} className={`h-1 transition-all rounded-full ${i === slide ? "w-14 bg-shramik-gold" : "w-6 bg-white/30 hover:bg-white/60"}`} aria-label={`slide ${i+1}`} />
              ))}
            </div>
            <div className="text-white/60 text-sm font-mono tracking-widest">{String(slide+1).padStart(2,"0")} / {String(HERO_SLIDES.length).padStart(2,"0")}</div>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-white border-y border-slate-200" data-testid="stats-bar">
        <div className="container-page grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-10 py-14 md:py-16">
          {IMPACT_STATS.map((s, i) => (
            <StatsCounter key={i} value={s.value} suffix={s.suffix} label={s.label} format={s.format} testId={`stat-${i}`} />
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-24 md:py-32" data-testid="about-preview">
        <div className="container-page grid lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{opacity:0,x:-30}} whileInView={{opacity:1,x:0}} viewport={{once:true}} transition={{duration:0.7}} className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
              <img src="https://images.weserv.nl/?url=shramikgroup.in/img/shramik1.jpg&w=1200" alt="Shramik Group" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-shramik-green/60 to-transparent" />
            </div>
            <div className="hidden md:block absolute -mt-24 ml-8">
              <div className="glass rounded-2xl p-6 max-w-xs shadow-2xl">
                <div className="text-3xl font-display text-shramik-green">Since 1976</div>
                <div className="text-xs uppercase tracking-widest text-slate-600 mt-1">Building rural enterprise</div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{opacity:0,x:30}} whileInView={{opacity:1,x:0}} viewport={{once:true}} transition={{duration:0.7}} className="lg:col-span-7">
            <div className="overline mb-5">About Shramik Group</div>
            <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-shramik-ink text-balance">
              A diversified enterprise <em className="not-italic font-serif italic text-shramik-green">rooted in rural India</em>, engineered for the modern economy.
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              Founded in 1976 by Mr. Sahebrao Ramchandra Navale Patil, Shramik Group operates across Agri-Inputs Trading, Milk & Food Processing, Building Materials, Stone Mining, Fuel Retailing and Education — a family-run enterprise driven by ethical business and long-term farmer relationships.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6">
              {[
                ["Producer-first", "4,500+ farmer families supply milk daily; zero third-party purchase."],
                ["Direct payments", "72 own collection centers with testing labs; payments to farmers' bank accounts."],
                ["Locally rooted", "14 Krushi Seva Kendra branches across Ahilyanagar, Nashik and Pune."],
                ["Future-ready", "₹120 Cr expansion — GEA-supplied SCADA milk-powder plant on order."],
              ].map(([t,d]) => (
                <div key={t} className="border-l-2 border-shramik-green pl-4">
                  <div className="font-display font-medium text-shramik-ink">{t}</div>
                  <div className="text-sm text-slate-600 mt-1">{d}</div>
                </div>
              ))}
            </div>
            <Link to="/about" data-testid="about-read-more" className="mt-10 inline-flex items-center gap-2 text-shramik-green font-semibold link-underline">Read our full story <ArrowRight className="w-4 h-4"/></Link>
          </motion.div>
        </div>
      </section>

      {/* VERTICALS BENTO */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-white via-shramik-greenLight/30 to-white" data-testid="verticals-section">
        <div className="container-page">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="overline mb-4">Our Businesses</div>
              <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl text-balance">Five verticals. <em className="not-italic font-serif italic text-shramik-green">One mission.</em></h2>
            </div>
            <Link to="/businesses" className="btn-secondary self-start md:self-end shrink-0">All verticals <ArrowRight className="w-4 h-4"/></Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-5 md:gap-6">
            {VERTICALS.map((v, i) => {
              const span = i === 0 ? "md:col-span-4 md:row-span-2 aspect-[16/12]" :
                           i === 1 ? "md:col-span-2 aspect-[4/5]" :
                           i === 2 ? "md:col-span-2 aspect-[4/5]" :
                           i === 3 ? "md:col-span-3 aspect-[16/9]" :
                                     "md:col-span-3 aspect-[16/9]";
              return (
                <Link
                  key={v.slug} to={`/businesses/${v.slug}`}
                  data-testid={`vertical-card-${v.slug}`}
                  className={`group relative rounded-3xl overflow-hidden ${span} card-hover`}
                >
                  <img src={v.image} alt={v.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.06]"/>
                  <div className={`absolute inset-0 bg-gradient-to-t ${v.color} opacity-90`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-shramik-ink/85 via-shramik-ink/30 to-transparent" />
                  <div className="relative h-full flex flex-col justify-end p-7 md:p-9 text-white">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-shramik-gold mb-3">{v.tag}</div>
                    <h3 className="font-display font-medium text-2xl md:text-3xl lg:text-4xl leading-tight max-w-md text-balance">{v.name}</h3>
                    <p className="mt-3 text-white/80 max-w-lg text-sm md:text-base hidden md:block">{v.short}</p>
                    <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white">
                      Explore <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1"/>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-24 md:py-32" data-testid="why-choose">
        <div className="container-page">
          <div className="max-w-3xl mb-14">
            <div className="overline mb-4">Why Shramik</div>
            <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-balance">
              Built on trust. <em className="not-italic font-serif italic text-shramik-green">Driven by outcomes.</em>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_CHOOSE.map((w, i) => {
              const Icon = ICONS[w.icon] || Sprout;
              return (
                <motion.div
                  key={w.title}
                  initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}}
                  transition={{duration:0.5, delay: i * 0.06}}
                  data-testid={`why-card-${i}`}
                  className="group relative rounded-2xl border border-slate-200 p-8 hover:border-shramik-green transition-all duration-500 hover:shadow-xl hover:-translate-y-1 bg-white"
                >
                  <div className="w-12 h-12 rounded-xl bg-shramik-greenLight text-shramik-green flex items-center justify-center mb-6 group-hover:bg-shramik-green group-hover:text-white transition-colors">
                    <Icon className="w-6 h-6" strokeWidth={1.75}/>
                  </div>
                  <h3 className="font-display text-xl font-medium mb-2">{w.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{w.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LEADERSHIP PREVIEW */}
      <section className="py-24 md:py-32 bg-shramik-ink text-white overflow-hidden relative" data-testid="leadership-preview">
        <div className="orb bg-shramik-green/30 w-[500px] h-[500px] top-0 -left-40" />
        <div className="container-page relative">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-semibold text-shramik-gold mb-4">Leadership</div>
              <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl text-balance">The people <em className="not-italic font-serif italic text-shramik-gold">behind the vision.</em></h2>
            </div>
            <Link to="/leadership" data-testid="leadership-cta" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold hover:bg-white hover:text-shramik-ink transition-colors self-start md:self-end shrink-0">View leadership team <ArrowRight className="w-4 h-4"/></Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-start max-w-5xl mx-auto">
            {LEADERSHIP.slice(0,3).map((p, i) => {
              // Hierarchy: 1st = Chairman (largest), 2nd, 3rd — descending prominence left → right
              const isPrimary = i === 0;
              const label = i === 0 ? "Founder & Chairman" : i === 1 ? "Director" : "Director";
              return (
                <motion.div
                  key={p.name}
                  initial={{opacity:0, y:40}}
                  whileInView={{opacity:1, y:0}}
                  viewport={{once:true}}
                  transition={{duration:0.6, delay: i*0.1}}
                  className={`group rounded-3xl overflow-hidden bg-white/5 border border-white/10 ${isPrimary ? "shadow-2xl shadow-shramik-green/20 ring-1 ring-shramik-gold/40 md:-mt-6" : i === 1 ? "md:mt-4" : "md:mt-10"}`}
                  data-testid={`leader-card-${i}`}
                >
                  <div className="aspect-[3/4] overflow-hidden bg-white/5">
                    <img src={p.photo} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <div className={`text-[10px] uppercase tracking-[0.28em] font-bold mb-1.5 ${isPrimary ? "text-shramik-gold" : "text-shramik-gold/70"}`}>{label}</div>
                    <div className={`font-display font-medium leading-tight ${isPrimary ? "text-lg md:text-xl" : "text-base md:text-lg"}`}>{p.name}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CSR STRIP */}
      <section className="py-20 md:py-24 bg-shramik-greenLight/60" data-testid="csr-strip">
        <div className="container-page grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Social Impact</div>
            <h3 className="font-display font-light text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight text-balance">
              Success we <em className="not-italic font-serif italic text-shramik-green">give back</em>, every year.
            </h3>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              ["Education", "7 Shramshakti institutions — schools, junior college and 4 degree colleges."],
              ["Foster Home", "Founder personally runs a foster home for 25+ years — 50+ current, 2,500+ past children thriving."],
              ["Cattle Feed & Vet", "~700 MT cattle feed supplied monthly plus veterinary services to producer members."],
              ["Direct Payments", "72 milk collection centers with labs — every farmer paid directly into their bank account."],
            ].map(([t,d]) => (
              <div key={t} className="bg-white/70 backdrop-blur rounded-2xl p-6 border border-white">
                <div className="font-display font-medium text-lg">{t}</div>
                <div className="text-sm text-slate-600 mt-1">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BRANCHES STRIP */}
      <section className="py-16 border-y border-slate-200" data-testid="branches-strip">
        <div className="container-page">
          <div className="flex items-center gap-3 text-shramik-green mb-6">
            <MapPin className="w-4 h-4"/> <span className="overline">Our footprint</span>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-3 font-display text-lg md:text-2xl text-slate-500">
            {BRANCHES.map((b, i) => (
              <span key={b} className="hover:text-shramik-green transition-colors">
                {b}{i < BRANCHES.length - 1 && <span className="ml-6 text-slate-300">•</span>}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32" data-testid="home-cta">
        <div className="container-page">
          <div className="relative rounded-[32px] overflow-hidden bg-shramik-ink px-8 md:px-16 py-16 md:py-24">
            <div className="orb bg-shramik-green/40 w-[500px] h-[500px] -bottom-40 -right-20"/>
            <div className="orb bg-shramik-blue/30 w-[400px] h-[400px] -top-20 -left-20"/>
            <div className="relative max-w-3xl">
              <div className="text-xs uppercase tracking-[0.22em] text-shramik-gold mb-6">Let's collaborate</div>
              <h2 className="font-display font-light text-white text-4xl md:text-6xl lg:text-7xl leading-[1.03] tracking-tight text-balance">
                Ready to grow with <em className="not-italic font-serif italic text-shramik-gold">Shramik Group?</em>
              </h2>
              <p className="mt-6 text-white/80 text-lg max-w-xl">Partnerships, distribution, careers, or CSR collaborations — we'd love to hear from you.</p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contact" data-testid="cta-primary" className="btn-primary bg-shramik-gold text-shramik-ink hover:bg-white">Get in touch <ArrowRight className="w-4 h-4"/></Link>
                <Link to="/careers" data-testid="cta-secondary" className="btn-secondary border-white text-white hover:bg-white/10">Explore careers</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
